import { useQuery, queryOptions } from "@tanstack/react-query";
import { apiClient, signApiRequest } from "./";
import { AxiosError } from "axios";
import { CustomerCredentials } from "../ui/customerDataStore";
import { captureException } from "@sentry/react";

export const getInsuranceById = async ({
  credentials,
  insuranceIdentifier,
}: {
  credentials: CustomerCredentials;
  insuranceIdentifier: string;
}) => {
  const signedRequest = await signApiRequest(
    "GET",
    `${import.meta.env.VITE_API_URL}/pension/insurance-details?fid=${credentials.identityId}&insuranceIdentifier=${insuranceIdentifier}`,
    credentials,
    {
      "content-type": "application/json",
    },
  );

  const response = await apiClient(signedRequest);

  return response.data;
};

export const getInsuranceQueryOptions = (
  credentials: CustomerCredentials,
  insuranceIdentifier: string,
) =>
  queryOptions({
    queryKey: ["get-insurance-by-id", insuranceIdentifier],
    queryFn: () => getInsuranceById({ credentials, insuranceIdentifier }),
    retry: false,
    throwOnError: (error) => {
      captureException(error, {
        tags: {
          operation: "get-insurance-by-id",
          errorType: error.name || "UnknownError",
        },
        extra: {
          apiEndpoint: `${import.meta.env.VITE_API_URL}/pension/insurance-details?fid=${credentials.identityId}&insuranceIdentifier=${insuranceIdentifier}`,
        },
        level: "error",
      });
      return true;
    },
  });

export const useGetInsuranceById = ({
  credentials,
  insuranceIdentifier,
  enabled,
}: {
  credentials: CustomerCredentials;
  insuranceIdentifier: string;
  enabled?: boolean;
}) => {
  const customerInfoQuery = useQuery<TInsurance, AxiosError>({
    queryKey: ["get-insurance-by-id", insuranceIdentifier],
    queryFn: () => getInsuranceById({ credentials, insuranceIdentifier }),
    enabled,
    retry: false,
    throwOnError: (error) => {
      captureException(error, {
        tags: {
          operation: "get-insurance-by-id",
          errorType: error.name || "UnknownError",
        },
        extra: {
          apiEndpoint: `${import.meta.env.VITE_API_URL}/pension/insurance-details?fid=${credentials.identityId}&insuranceIdentifier=${insuranceIdentifier}`,
        },
        level: "error",
      });
      return true;
    },
  });
  return customerInfoQuery;
};

interface TInsurance {
  agreementNumber: string;
  insuranceEndDate: string;
  insuranceIdentifier: string;
  isCapitalPension: boolean;
  changedBy: string;
  isInPbfState: boolean;
  isDC: boolean;
  alternativeChoice: string;
  benefitGroup: string;
  isSalarySacrifice: boolean;
  businessName: string;
  businessType: string;
  status: string;
  calculationModel: string;
  contributionSalary: number;
  createdAt: string;
  transactionId: string;
  zAge: number;
  zDate: string;
  paymentPeriod: number;
  description: string;
  paymentProtectionPeriod: number;
  plan: string;
  planDepartment: string;
  planDetail: string;
  exceptions: TInsuranceException[];
  platform: string;
  fee: number;
  premium: number;
  fixedFee: number;
  premiumEndDate: string;
  premiumTerm: string;
  fundIdentifier: string;
  hasPaymentProtection: boolean;
  reference: string;
  elements: TInsuranceElement[];
  insuranceCompany?: TInsuranceCompany;
  parties: TInsuranceParty[];
  managementType: string;
}

interface TEventCover {
  deathPUPAmountInclRefund: number;
  deathPrcAmount: number;
  deathAmountExclRefund: number;
  deathAmountForPaymentTerm: number;
  deathAmountInclRefund: number;
  deathPUPAmountExclRefund: number;
  type: string;
  relType: string;
  assets: TInsuranceAsset[];
}

interface TInsurancePayoutTiming {
  pattern: string;
  period: number;
}

interface TInsuranceException {
  code: string;
  message: string;
}

interface TInsuranceBenefit {
  calcPUPGuaranteedAmountExclRefund: number;
  calcPUPGuaranteedAmountExclRefundDate: string;
  calcPUPGuaranteedAmountInclRefund: number;
  calcPUPGuaranteedAmountInclRefundDate: string;
  currentRepurchaseValue: number;
  currentRetrospectiveReserveValue: number;
  amount: number;
  projectedAmount: number;
  guaranteedAmountExclRefund: number;
  projectedAmountPaymentPeriod: number;
  guaranteedAmountInclRefund: number;
  benefitValue: number;
  assets: TInsuranceAsset[];
  monthlyBenefitValue?: number;
  relType: string;
}

interface TInsuranceAsset {
  isin: string;
  name: string;
  marketValue: number;
  share: number;
  sekNav: number;
  bbType: string;
  bbName: string;
}

interface TInsuranceCompany {
  organisationId: string;
  uuid: string;
  name: string;
  phone: string;
  logotype: string;
  logotype_trans: string;
}

interface TInsuranceElement {
  eventNodes: (TInsuranceBenefit | TEventCover)[];
  relation: string;
  insuranceIdentifier: string;
  isActive: boolean;
  hasPbf: boolean;
  managementTypeDetail: string;
  servingTimeFactor: number;
  status: string;
  calculationModel: string;
  taxClass: string;
  coInsured: string;
  coInsuredSSN: string;
  type: string;
  managementType: string;
  uuid: string;
  otherOwner: string;
  zDate: string;
  paymentPeriod: number;
  depotManager: string;
  payoutTiming: TInsurancePayoutTiming;
  elementDate: string;
  elementID: string;
  elementType: string;
  plan: string;
  planDescription: string;
  eventType: string;
  planNumber: string;
  eventTypeDate: string;
  fee: number;
  premium: number;
  fixedFee: number;
  premiumEndDate: string;
  premiumFee: number;
  hasClause: string;
}

interface TInsuranceParty {
  lastName?: string;
  firstName?: string;
  legalName?: string;
  identity: string;
  relType: string;
}

export type { TInsurance, TInsuranceAsset, TInsuranceElement, TInsuranceParty };
